import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';
import { z } from 'zod';

import { formatInstant } from '@/shared/format/date';
import { defineBlockComponent, type BlockComponentProps } from '@/shared/page';
import { readPath, resolvePatternText, textSchema } from '@/shared/patterns';
import { cn } from '@/shared/ui/cn';

/** `done` terminou, `current` começou e não terminou, `pending` não começou, `failed` parou por problema. */
export const TIMELINE_STATES = ['done', 'current', 'pending', 'failed'] as const;
export type TimelineState = (typeof TIMELINE_STATES)[number];

/** Um passo ou evento da trilha (P20). Nome e descrição vêm da API como vieram (U33). */
export interface TimelineItem {
  readonly id: string;
  readonly title: string;
  readonly description?: string | null | undefined;
  readonly state: TimelineState;
  /** Instante ISO. */
  readonly startedAt?: string | null | undefined;
  readonly startedBy?: string | null | undefined;
  readonly finishedAt?: string | null | undefined;
  readonly finishedBy?: string | null | undefined;
  /** Linhas extras (as alterações de uma auditoria: "Nome de A para B"). */
  readonly details?: readonly string[] | undefined;
}

/** A regra do legado (`components/Timeline`): com fim, feito; só com início, em andamento; sem datas, não iniciado. */
export function timelineState(startedAt: unknown, finishedAt: unknown): TimelineState {
  if (typeof finishedAt === 'string' && finishedAt !== '') return 'done';
  if (typeof startedAt === 'string' && startedAt !== '') return 'current';
  return 'pending';
}

const DOT: Readonly<Record<TimelineState, string>> = {
  done: 'border-ok bg-ok',
  current: 'border-primary bg-primary',
  pending: 'border-input bg-card',
  failed: 'border-err bg-err',
};

function when(i18n: I18n, at: string, compact: boolean): string {
  return formatInstant(at, { locale: i18n.locale, seconds: !compact }) ?? at;
}

function Line({ label, at, by, compact, strong }: { readonly label: 'started' | 'finished'; readonly at: string; readonly by: string | null | undefined; readonly compact: boolean; readonly strong: boolean }) {
  const { i18n, t } = useLingui();
  const time = when(i18n, at, compact);
  const text = label === 'started' ? (by ? t`Iniciado em ${time} por ${by}` : t`Iniciado em ${time}`) : by ? t`Finalizado em ${time} por ${by}` : t`Finalizado em ${time}`;
  // Sobre o `accent-wash` da etapa atual, o cinza não passa no contraste AA.
  return <p className={cn('text-sm tabular-nums', strong ? 'text-foreground' : 'text-muted-foreground')}>{text}</p>;
}

export interface TimelineProps {
  readonly items: readonly TimelineItem[];
  /** iPhone: datas sem segundos e respiro menor (protótipo `m_history`). */
  readonly compact?: boolean;
  /** Nome acessível da lista. */
  readonly label?: string;
  readonly className?: string;
}

/**
 * Linha do tempo vertical (P20): um item por etapa ou evento, com "Iniciado em … por …",
 * "Finalizado em … por …" ou "Etapa não iniciada". A etapa em andamento ganha destaque e
 * `aria-current`. O componente é burro: quem decide o estado é o módulo (ou `timelineState`).
 */
export function Timeline({ items, compact = false, label, className }: TimelineProps) {
  const { t } = useLingui();
  return (
    <ol aria-label={label ?? t`Linha do tempo`} data-slot="timeline" className={cn('flex flex-col', className)}>
      {items.map((item, index) => {
        const last = index === items.length - 1;
        const pending = item.state === 'pending';
        return (
          <li key={item.id} className="flex gap-3.5" {...(item.state === 'current' ? { 'aria-current': 'step' as const } : {})}>
            <div aria-hidden className="flex w-3.5 shrink-0 flex-col items-center">
              <span className={cn('mt-3.5 size-3.5 shrink-0 rounded-full border-2', DOT[item.state])} />
              {last ? null : <span className="mt-1 w-0.5 flex-1 bg-border" />}
            </div>
            <div className={cn('mb-1.5 flex min-w-0 flex-1 flex-col gap-0.5 rounded-lg px-3.5 py-2.5', item.state === 'current' && 'bg-accent-wash')}>
              <p className={cn('text-base font-semibold', pending ? 'text-muted-foreground' : 'text-foreground')}>{item.title}</p>
              {item.description ? <p className="text-sm text-foreground">{item.description}</p> : null}
              {pending && !item.startedAt ? <p className="text-sm text-muted-foreground">{t`Etapa não iniciada`}</p> : null}
              {item.startedAt ? <Line label="started" at={item.startedAt} by={item.startedBy} compact={compact} strong={item.state === 'current'} /> : null}
              {item.finishedAt ? <Line label="finished" at={item.finishedAt} by={item.finishedBy} compact={compact} strong={item.state === 'current'} /> : null}
              {item.details && item.details.length > 0 ? (
                <ul className="mt-1 list-disc pl-4 text-sm text-muted-foreground">
                  {item.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─── genérico de bloco ──────────────────────────────────────────────────────

/** Onde está cada coisa no dado da fonte (caminhos com ponto), para a configuração ser JSON. */
export const timelineBlockProps = z.object({
  /** Caminho da lista no dado (`timeline`); vazio = o próprio dado é a lista. */
  items: z.string(),
  title: z.string().min(1),
  description: z.string().optional(),
  startedAt: z.string().optional(),
  startedBy: z.string().optional(),
  finishedAt: z.string().optional(),
  finishedBy: z.string().optional(),
  /** Nome acessível da lista. */
  label: textSchema.optional(),
});
export type TimelineBlockProps = z.infer<typeof timelineBlockProps>;

const text = (row: unknown, path: string | undefined): string | undefined => {
  if (!path) return undefined;
  const value = readPath(row, path);
  return typeof value === 'string' && value !== '' ? value : undefined;
};

/** Linhas do dado → itens, com o estado pela regra do legado. */
export function timelineItemsFrom(data: unknown, props: TimelineBlockProps): TimelineItem[] {
  const list = props.items === '' ? data : readPath(data, props.items);
  if (!Array.isArray(list)) return [];
  return list.map((row: unknown, index) => {
    const startedAt = text(row, props.startedAt);
    const finishedAt = text(row, props.finishedAt);
    return {
      id: `${String(index)}-${text(row, props.title) ?? ''}`,
      title: text(row, props.title) ?? '',
      description: text(row, props.description),
      state: timelineState(startedAt, finishedAt),
      startedAt,
      startedBy: text(row, props.startedBy),
      finishedAt,
      finishedBy: text(row, props.finishedBy),
    };
  });
}

type TimelineView = BlockComponentProps<TimelineBlockProps, object>;

function TimelineBlockWeb({ props, data }: TimelineView) {
  const { i18n } = useLingui();
  return <Timeline items={timelineItemsFrom(data, props)} {...(props.label ? { label: resolvePatternText(i18n, props.label) } : {})} />;
}

function TimelineBlockMobile({ props, data }: TimelineView) {
  const { i18n } = useLingui();
  return <Timeline compact items={timelineItemsFrom(data, props)} {...(props.label ? { label: resolvePatternText(i18n, props.label) } : {})} />;
}

/** `Timeline` como genérico do catálogo, sobre uma fonte `record` que traz a lista de etapas. */
export const TimelineBlock = defineBlockComponent<TimelineBlockProps, object>({
  id: 'Timeline',
  description: 'Vertical timeline of steps or events read from a list in the record: title, description, started and finished time and actor.',
  props: timelineBlockProps,
  render: { web: TimelineBlockWeb, mobile: TimelineBlockMobile },
});
